import React, { useEffect, useMemo, useState } from "react";
import styles from "./studio.module.scss";
import _ from "lodash";
import cx from 'classnames';

import { Podcasts, Simulation } from "./card";
import { ScrollableContainer } from "src/components/scroller";
import { useDispatch } from "react-redux";
import { fetchDraftCourses, fetchPublishedCourses } from "src/redux/actions/coaching/studio";
import InfiniteScroll from "src/components/infiniteScroll";
import { useSelector } from "react-redux";
import { DraftCourseSummary } from "src/types/coaching/courses";
import CreateSimulationDialog from "../manage/simulations/components/simulationTypeDialog";
import { createSimulation, invalidateSimulationCreationState } from "src/redux/actions/coaching/simulation";
import { useNavigate } from "react-router-dom";
import { createPodcast, invalidatePodcastCreationState } from "src/redux/actions/coaching/podcast";
import Button from "src/components/button";
import {
  buildOriginNavigationState,
  COACHING_STUDIO_PATH,
  getDraftEditPath,
} from "src/pages/coaching/utils/manageNavigation";

const getStudioSimulationDraftPath = (simulationId?: string) => {
  if (!simulationId) {
    return "";
  }

  return `/coaching/manage/simulations/draft/${simulationId}`;
};

const getStudioSimulationOverviewPath = (simulationId?: string) => {
  if (!simulationId) {
    return "";
  }

  return `/coaching/manage/simulations/published/${simulationId}`;
};

const getStudioPodcastOverviewPath = (podcastId?: string) => {
  if (!podcastId) {
    return "";
  }

  return `/coaching/manage/podcasts/published/${podcastId}`;
};

export const StudioPage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showCreateSimulationDialog, setShowCreateSimulationDialog] = React.useState(false);

  const { creatingSimulation, createdSimulation } = useSelector((state) => {
    const creatingSimulation = _.get(state, 'coaching.simulations.creation.creating');
    const createdSimulation = _.get(state, 'coaching.simulations.creation.item');

    return {
      creatingSimulation,
      createdSimulation
    };
  }, _.isEqual);

  const { creatingPodcast, createdPodcast } = useSelector((state: any) => {
    const creatingPodcast = _.get(state.coaching, "podcasts.creation.creating");
    const createdPodcast = _.get(state.coaching, "podcasts.creation.item");

    return {
      creatingPodcast,
      createdPodcast,
    };
  }, _.isEqual);

  const { loading } = useSelector((state) => {
    const coursesState = _.get(state, `coaching.studio.drafts`);
    const loading = _.get(coursesState, 'loading') || false;

    return {
      loading
    };
  }, _.isEqual);

  const featuredCourse = useSelector((state) => {
    const featured = _.get(state, `coaching.studio.featured`) || null;
    return featured as DraftCourseSummary | null;
  });

  const handleCreateSimulation = (type: string) => {
    dispatch(createSimulation({
      name: 'Untitled Simulation',
      type: type
    }));
  }

  const handleCreatePodcast = () => {
    dispatch(
      createPodcast({
        name: "Untitled Podcast",
      }),
    );
  };

  useEffect(() => {
    if (createdSimulation) {
      const id = _.get(createdSimulation, 'id');
      dispatch(invalidateSimulationCreationState());
      navigate(getDraftEditPath("simulation", id), {
        state: buildOriginNavigationState(COACHING_STUDIO_PATH),
      });

      setShowCreateSimulationDialog(false);
    }
  }, [createdSimulation]);

  useEffect(() => {
    if (createdPodcast) {
      const id = _.get(createdPodcast, "id");
      dispatch(invalidatePodcastCreationState());
      navigate(getDraftEditPath("podcast", id), {
        state: buildOriginNavigationState(COACHING_STUDIO_PATH),
      });
    }
  }, [createdPodcast]);

  useEffect(() => {
    dispatch(fetchDraftCourses({
      courseTypes: ["podcast", "simulation"],
      page: 0,
      limit: 10,
    }));
  }, [dispatch]);

  return (
    <div className={styles.container}>
      <div className={styles.creationRow}>
        <div className={styles.recentlyEdited}>
          {loading && !featuredCourse && (
            <>
              <h2>experience that you created recently...</h2>
              <div className={styles.entity}>
                <Simulation.PlaceholderFeatured />
              </div>
            </>
          )}
          {featuredCourse && (
            <>
              <h2>experience that you created recently...</h2>
              <div className={styles.entity}>
                {featuredCourse?.entityType === "podcast" &&
                  <Podcasts.Featured podcast={featuredCourse?.course} url={`/coaching/manage/podcasts/draft/${featuredCourse?.course?.id}`} />
                }
                {featuredCourse?.entityType === "simulation" &&
                  <Simulation.Featured
                    simulation={featuredCourse?.course}
                    url={getStudioSimulationDraftPath(featuredCourse?.course?.id)}
                  />
                }
              </div>
            </>
          )}
        </div>
        <div className={styles.actions}>
          <button className={cx(styles.create, { [styles.creating]: creatingPodcast })}
            disabled={creatingPodcast || creatingSimulation}
            onClick={handleCreatePodcast}
          >
            <img src="/images/coaching/podcast_host.svg" width={110} height={110} alt="Create Podcast" />
            <i className={cx(styles.icon, styles.mic,  'paperflite-mic')} />
            <div className={styles.name}>{creatingPodcast ? 'creating podcast...' : 'create podcast'}</div>
            <p className={styles.description}> choose your deep dive conversation based on PDFs, websites, text <br />and many more...</p>
          </button>
          <button className={cx(styles.create, { [styles.creating]: creatingSimulation })}
            disabled={creatingPodcast || creatingSimulation}
            onClick={() => setShowCreateSimulationDialog(true)}
          >
            <img src="/images/coaching/create_simulation.svg" width={110} height={110} alt="Create Simulation" />
            <i className={cx(styles.icon, 'paperflite-phone-call')} />
            <div className={styles.name}>{creatingSimulation ? 'creating call simulation...' : 'create call simulation'}</div>
            <p className={styles.description}>Create realistic call scenarios for your reps to practice. Perfect for dry runs that build skills and confidence—no pressure, just prep and more wins</p>
          </button>
        </div>
      </div>
      <DraftCourses />
      <PublishedCourses />
      <CreateSimulationDialog
        show={showCreateSimulationDialog}
        handleClose={() => setShowCreateSimulationDialog(false)}
        handleSelect={handleCreateSimulation}
      />
    </div>
  );
}

const DraftCourses: React.FC<object> = () => {
  const dispatch = useDispatch();

  const [courseTypes, setCourseTypes] = useState<string[]>([]);

  const { courses, loading, hasMoreItems, nextPage, total } = useSelector((state) => {
    const coursesState = _.get(state, `coaching.studio.drafts`);

    const courses = _.get(coursesState, 'items') || [] as DraftCourseSummary[];
    const loading = _.get(coursesState, 'loading') || false;
    const pagination = _.get(coursesState, 'pagination');
    const total = _.get(pagination, "totalRecordsCount", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems") || false;

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      courses,
      loading,
      hasMoreItems,
      nextPage,
      total
    };
  }, _.isEqual);

  const fetchCoursesForPage = (courseTypes: string[], page: number): void => {
    if (courseTypes.length === 0) {
      courseTypes = ['podcast', 'simulation'];
    }
    dispatch(fetchDraftCourses({ courseTypes: courseTypes, page, limit: 10 }));
  };

  const handleLoadMoreCourses = () => {
    if (nextPage !== null) fetchCoursesForPage(courseTypes, nextPage);
  };

  useEffect(() => {
    fetchCoursesForPage(courseTypes, 0);
  }, []);

  const toggleCourseType = (type: string) => {
    let updatedTypes = [...courseTypes];
    if (courseTypes.includes(type)) {
      updatedTypes = courseTypes.filter((t) => t !== type);
    } else {
      updatedTypes = [...courseTypes, type];
    }

    setCourseTypes(updatedTypes);
    fetchCoursesForPage(updatedTypes, 0);
  };

  if (!loading && _.size(courses) === 0) {
    return null;
  }

  return (
    <div className={styles.draftsRow}>
      <header className={styles.draftsHeader}>
        <h2>continue from where you stopped creating</h2>
        <div className={styles.actions}>
          <Button variant='secondary'
            className={cx(styles.action, {
              [styles.active]: courseTypes.includes('podcast')
            })}
            icon={courseTypes.includes('podcast') ? "paperflite-tick" : undefined}
            text="Podcast"
            onClick={() => toggleCourseType('podcast')}
          />
          <Button variant='secondary'
            className={cx(styles.action, {
              [styles.active]: courseTypes.includes('simulation')
            })}
            icon={courseTypes.includes('simulation') ? "paperflite-tick" : undefined}
            text="Simulation"
            onClick={() => toggleCourseType('simulation')}
          />
        </div>
      </header>
      <ScrollableContainer distance={300} wrapperClassName={styles.draftsList} leftArrowComponent={LeftArrow} rightArrowComponent={RightArrow}>
        <InfiniteScroll
          className={styles.courseList}
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={handleLoadMoreCourses}
          loader={() => {
            return _.times(_.size(courses) == 0 ? 3 : 1).map((i) => (
              <Simulation.PlaceholderCard key={i} />
            ));
          }}
        >
          {courses.map((course, index) => {
            if (course?.entityType === "simulation") {
              const url = `/coaching/manage/simulations/draft/${course?.course?.id}`;
              return <Simulation.Card key={course.id || index} simulation={course?.course} url={url} status="draft" />
            }

            if (course?.entityType === "podcast") {
              const url = `/coaching/manage/podcasts/draft/${course?.course?.id}`;
              return <Podcasts.Card key={course.id || index} podcast={course?.course} url={url} status="draft" />
            }

            return false;
          })}
        </InfiniteScroll>
      </ScrollableContainer>
    </div>
  );
}

const PublishedCourses: React.FC<object> = () => {
  const dispatch = useDispatch();

  const { courses, loading, hasMoreItems, nextPage, total } = useSelector((state) => {
    const coursesState = _.get(state, `coaching.studio.published`);

    const courses = _.get(coursesState, 'items') || [] as DraftCourseSummary[];
    const loading = _.get(coursesState, 'loading') || false;
    const pagination = _.get(coursesState, 'pagination');
    const total = _.get(pagination, "totalRecordsCount", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems") || false;

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      courses,
      loading,
      hasMoreItems,
      nextPage,
      total
    };
  }, _.isEqual);

  const fetchCoursesForPage = (page: number): void => {
    dispatch(fetchPublishedCourses({
      courseTypes: ['simulation', 'podcast'],
      page,
      limit: 15
    }));
  };

  const handleLoadMoreCourses = () => {
    if (nextPage !== null) fetchCoursesForPage(nextPage);
  };

  useEffect(() => {
    fetchCoursesForPage(0);
  }, []);

  if (!loading && _.size(courses) === 0) {
    return null;
  }

  return (
    <div className={styles.publishedRow}>
      <header className={styles.publishedHeader}>
        <h2>experiences you have created</h2>
      </header>
      <ScrollableContainer distance={300} wrapperClassName={styles.publishedList} leftArrowComponent={LeftArrow} rightArrowComponent={RightArrow}>
        <InfiniteScroll
          className={styles.courseList}
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={handleLoadMoreCourses}
          loader={() => {
            return _.times(_.size(courses) == 0 ? 3 : 1).map((i) => (
              <Simulation.PlaceholderCard key={i} />
            ));
          }}
        >
          {courses.map((course, index) => {
            if (course?.entityType === "simulation") {
              const url = getStudioSimulationOverviewPath(course?.course?.id);
              return <Simulation.Card key={course.id || index} simulation={course?.course} url={url} status="published" />
            }

            if (course?.entityType === "podcast") {
              const url = getStudioPodcastOverviewPath(course?.course?.id);
              return <Podcasts.Card key={course.id || index} podcast={course?.course} url={url} status="published" />
            }

            return false;
          })}
        </InfiniteScroll>
      </ScrollableContainer>
    </div>
  );
}

const LeftArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.left)}>
      <i className={cx(styles.icon, "paperflite-left-arrow")}></i>
    </div>
  );
}

const RightArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.right)}>
      <i className={cx(styles.icon, "paperflite-right-arrow")}></i>
    </div>
  );
}